import { useState, useEffect, useCallback, memo } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useCompanyCount } from '@/hooks/useCompanies';
import { useLanguage } from '@/contexts/LanguageContext';
import faulknerIcon from '@/assets/faulkner-group-icon.png.asset.json';

interface HeroSectionProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
}

export const HeroSection = memo(function HeroSection({
  searchQuery,
  onSearchChange
}: HeroSectionProps) {
  // Local state for immediate input responsiveness
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const { data: companyCount } = useCompanyCount();
  const { t } = useLanguage();

  // Sync local state when parent searchQuery changes (e.g., on clear)
  useEffect(() => {
    if (searchQuery !== localSearch) {
      setLocalSearch(searchQuery);
    }
  }, [searchQuery]);

  // Debounced callback to parent
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== searchQuery) {
        onSearchChange(localSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearch, onSearchChange, searchQuery]);
  return <section className="relative overflow-hidden bg-gradient-subtle py-20 md:py-28">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-coral/10 blur-3xl animate-float" />
        <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-accent/10 blur-3xl animate-float" style={{
        animationDelay: '2s'
      }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-96 w-96 rounded-full bg-lavender/5 blur-3xl" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-block mb-4 px-4 py-1.5 text-sm font-medium text-primary bg-primary/10 rounded-full animate-fade-in">
            {t('Discover Women\'s Health Innovation')}
          </span>
          
          <h1 className="font-display text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl animate-fade-in" style={{
          animationDelay: '0.1s'
        }}>
            {t('The Global')}{' '}
            <span className="text-gradient">{t('Femtech')}</span>
            {' '}{t('Directory')}
          </h1>
          
          <p className="mt-6 text-lg text-muted-foreground md:text-xl animate-fade-in" style={{
          animationDelay: '0.2s'
        }}>
            {t('Explore innovative companies transforming women\'s health.')}
            <br />
            {t('Find solutions for fertility, pregnancy, menopause, and beyond.')}
          </p>

          {/* Search Bar */}
          <div className="mt-10 animate-fade-in" style={{
          animationDelay: '0.3s'
        }}>
            <div className="relative mx-auto max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input type="search" placeholder={t('Search companies, solutions, health areas...')} value={localSearch} onChange={e => setLocalSearch(e.target.value)} className="h-14 pl-12 pr-4 text-base rounded-2xl border-2 border-border bg-card shadow-md focus:border-primary focus:shadow-glow transition-all duration-300" />
            </div>
          </div>

          {/* Stats */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-sm animate-fade-in" style={{
          animationDelay: '0.4s'
        }}>
            <div className="text-center">
              <span className="block text-2xl font-bold text-foreground">
                {companyCount !== undefined ? `${companyCount}+` : '...'}
              </span>
              <span className="text-muted-foreground">{t('Companies')}</span>
            </div>
            <div className="h-8 w-px bg-border" />
            <div className="text-center">
              <span className="block text-2xl font-bold text-foreground">27</span>
              <span className="text-muted-foreground">{t('Categories')}</span>
            </div>
            <div className="h-8 w-px bg-border" />
            <div className="text-center">
              <span className="block text-2xl font-bold text-foreground">{t('Daily')}</span>
              <span className="text-muted-foreground">{t('Updates')}</span>
            </div>
          </div>

          {/* Attribution */}
          <div className="mt-8 animate-fade-in" style={{ animationDelay: '0.5s' }}>
            <a
              href="https://thefaulknergroupadvisors.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <span>{t('Created by')}</span>
              <img src={faulknerIcon.url} alt="" className="h-4 w-4 rounded-sm object-cover" />
              <span className="font-medium">The Faulkner Group</span>
            </a>
          </div>
        </div>
      </div>
    </section>;
});